import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiChevronDown, FiExternalLink, FiMenu } from 'react-icons/fi';
import { cSidebar, fullUrl } from '../../api';
import { useLanguage } from '../../i18n';
import { BLUE } from './brand';

/**
 * Community **left sidebar** — the "General" menu the admin builds.
 *
 * Every entry comes from the backend (`GET /api/community/sidebar`) and may have:
 * an emoji icon (or none), an optional picture, a main category, a link and an
 * "open in a new tab" switch. Entries without a link are shown as plain labels.
 */
export default function CommunitySidebar({ className = '', children = null }) {
  const { t, lang } = useLanguage();
  const [items, setItems] = useState([]);
  const [ready, setReady] = useState(false);
  const [closed, setClosed] = useState({});          // collapsed categories

  useEffect(() => {
    cSidebar()
      .then((res) => setItems(res?.items || []))
      .catch(() => setItems([]))
      .finally(() => setReady(true));
  }, []);

  // Nothing to show (or still loading) → keep the grid cell, so the centred
  // content and the right rail never jump around when the menu is empty.
  // `children` (the shops block) still render.
  if (!ready || items.length === 0) {
    if (!children) return <aside className={`${className} hidden lg:block`} aria-hidden="true" />;
  }

  // group by main category (entries with no category share one "General" group)
  const groups = [];
  items.forEach((item) => {
    const key = item.category ? `c${item.category.id}` : 'general';
    let group = groups.find((g) => g.key === key);
    if (!group) {
      const name = item.category
        ? (lang === 'kh' ? (item.category.name_km || item.category.name) : item.category.name)
        : t('generalMenu');
      group = { key, name, icon: item.category?.icon || '📁', items: [] };
      groups.push(group);
    }
    group.items.push(item);
  });

  const entry = (item) => {
    const label = lang === 'kh' ? (item.name_km || item.name) : item.name;
    const body = (
      <>
        {item.image ? (
          <img
            src={fullUrl(item.image)}
            alt={label}
            className="w-8 h-8 rounded-lg object-cover shrink-0"
          />
        ) : item.icon ? (
          <span className="w-8 h-8 rounded-lg flex items-center justify-center text-lg shrink-0 bg-gray-100 dark:bg-gray-800">
            {item.icon}
          </span>
        ) : null}
        <span className="flex-1 min-w-0 truncate font-semibold">{label}</span>
        {item.link && item.new_tab ? <FiExternalLink className="w-3.5 h-3.5 text-gray-400 shrink-0" /> : null}
      </>
    );
    const cls = 'flex items-center gap-2.5 px-2.5 py-2 rounded-xl text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition';

    if (!item.link) return <span key={item.id} className={cls}>{body}</span>;
    if (/^https?:/i.test(item.link)) {
      return (
        <a key={item.id} href={item.link} className={cls}
           target={item.new_tab ? '_blank' : undefined}
           rel={item.new_tab ? 'noreferrer' : undefined}>
          {body}
        </a>
      );
    }
    return (
      <Link key={item.id} to={item.link} className={cls}
            target={item.new_tab ? '_blank' : undefined}>
        {body}
      </Link>
    );
  };

  return (
    <aside className={`${className} hidden lg:block no-scrollbar space-y-3 sticky top-[72px] max-h-[calc(100vh-88px)] overflow-y-auto overscroll-contain`}>
      {items.length > 0 && (
        <section className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-3">
          <h3 className="flex items-center gap-2 font-extrabold text-sm px-2 py-1.5">
            <span className="w-6 h-6 rounded-lg flex items-center justify-center text-white text-xs" style={{ background: BLUE }}>
              <FiMenu className="w-3.5 h-3.5" />
            </span>
            {t('sideMenu')}
          </h3>

          {groups.map((group) => {
            const isClosed = closed[group.key];
            return (
              <div key={group.key} className="mt-1">
                <button
                  type="button"
                  onClick={() => setClosed((prev) => ({ ...prev, [group.key]: !isClosed }))}
                  className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-[12px] font-extrabold uppercase tracking-wide text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
                >
                  <span>{group.icon}</span>
                  <span className="flex-1 text-left truncate">{group.name}</span>
                  <FiChevronDown className={`w-4 h-4 transition ${isClosed ? '-rotate-90' : ''}`} />
                </button>
                {!isClosed && <div className="space-y-0.5">{group.items.map(entry)}</div>}
              </div>
            );
          })}
        </section>
      )}

      {/* Blocks handed in by the page (the **shops** list lives here) */}
      {children}
    </aside>
  );
}
